import React from 'react';

import {Set, Map, List, fromJS} from 'immutable';
import {useAjax} from './useAjax';
import {
  PHISHING_STATUS_OPTIONS,
  TRIAGE_ENDPOINT,
  TRIAGE_STATUS_OPTIONS,
} from './constants';
import DATE_CONSTANTS from 'trustar-2.0/utils/Date.constants';
import {PHISHING_SCORE_VALUES} from '../../../src/app/components/phishing/views/constants';
import {useCursorPagination} from './useCursorPagination';

export const defaultTriagePayload = Map({
  from: Date.parse(DATE_CONSTANTS.LAST_MONTH),
  to: Date.now(),
  enclaveIds: List([]),
  normalizedTriageScore: List(TRIAGE_STATUS_OPTIONS),
  normalizedIndicatorScore: List(TRIAGE_STATUS_OPTIONS),
  status: List(PHISHING_STATUS_OPTIONS)
});

/**
 * Main Ajax Hook for triage submissions fetching/filtering/sorting
 * @param enclaves
 * @param selectedTriageStatus
 * @param selectedDateRange
 * @returns {{totalItems: *, cursorControl: *, response: *, goBackward: *, goForward: *, refetch: *, error: *}}
 */
export const useTriageService = (enclaves, selectedTriageStatus, selectedDateRange) => {
  /* fetchCount is an integer used to retrigger a fetch
    (if the fetchCount number changes an ajax call with the current payload is fired)
   */
  const [fetchCount, setFetchCount] = React.useState(0);
  const triageServiceCursorPagination = useCursorPagination();
  const {goBackward, goForward, cursorForCurrentPage, reset: resetCursor, addNextCursor} = triageServiceCursorPagination;

  const [cursorInRequestPayload, setCursorForPayloadRequest] = React.useState('');
  const [totalItems, setTotalItems] = React.useState(0);

  const refetch = React.useCallback(() => {
    setFetchCount(fetchCount + 1);
  }, [fetchCount]);

  const enclaveIds = React.useMemo(
    () => enclaves.map(enc => enc.get('id')),
    [enclaves]
  );
  const ajax = useAjax(ajax => {

    return ajax
      .postJSON(TRIAGE_ENDPOINT,
        createSearchQuery(enclaveIds, selectedDateRange, selectedTriageStatus, cursorInRequestPayload));
  }, enclaves.size > 0);

  // $FlowFixMe: React.useEffect coming soon.
  React.useEffect(() => {
    ajax.get('executeRequest')();
  }, [selectedTriageStatus, selectedDateRange, cursorInRequestPayload, fetchCount]);

  // $FlowFixMe: React.useEffect coming soon.
  React.useEffect(() => {
    /* We always take an use a nextCursor value returned to us by the server because between paginating
      the query results may have changed and thus their cursor will be as well
    */
    const nextCursor = ajax.getIn(['response', 'responseMetadata', 'nextCursor']);
    if (nextCursor) addNextCursor(nextCursor);

    const updatedTotalItems = ajax.getIn(['response', 'responseMetadata', 'totalItems']);
    if (updatedTotalItems) {
      setTotalItems(updatedTotalItems);
    }
  }, [addNextCursor]);

  // $FlowFixMe: React.useEffect coming soon.
  React.useEffect(() => {
    setCursorForPayloadRequest(cursorForCurrentPage);
  }, [
    cursorForCurrentPage
  ]);

  return {
    ...ajax.toJS(),
    response: ajax.get('response'),
    error: ajax.get('errorResponse'),
    totalItems,
    goForward,
    goBackward,
    cursorControl: fromJS(triageServiceCursorPagination),
    refetch
  };
};

/**
 * Ajax hook for handling updates to a reports submission status (IGNORED, UNRESOLVED, CONFIRMED)
 * @returns {{response: *, setSubmissionStatus: *}}
 */
export const useTriageSubmissionStatusService = () => {
  const [currentPayload, setCurrentSubmissionPayload] = React.useState(Map({}));

  const ajax = useAjax(
    ajax =>
      ajax
        .post(`${TRIAGE_ENDPOINT}/${currentPayload.get('submissionId')}/status?status=${currentPayload.get('status')}`)
        .map(response => response),
    (currentPayload.has('submissionId') && currentPayload.has('status')) // Only fire request if both fields/params are there
  );

  const setSubmissionStatus = (submissionId: string, status: $Values<PHISHING_SCORE_VALUES>) => {
    setCurrentSubmissionPayload(Map({
      submissionId,
      status
    }));

    ajax.get('executeRequest')();
  };

  return {
    ...ajax.toJS(),
    response: ajax.get('response'),
    setSubmissionStatus
  };
};

/**
 * Base template for a triage query
 * @param enclaveIds
 * @param selectedDateRange
 * @param selectedTriageStatus
 * @param cursorInRequestPayload
 * @returns {*}
 */
export function createSearchQuery(
  enclaveIds,
  selectedDateRange,
  selectedTriageStatus,
  cursorInRequestPayload,
) {
  return defaultTriagePayload
    .set('from', Date.parse(selectedDateRange))
    .set('enclaveIds', enclaveIds)
    .set('cursor', cursorInRequestPayload)
    .set('status', List([].concat(selectedTriageStatus)));
}



// WEBPACK FOOTER //
// ./web_modules/trustar-2.0/hooks/useTriageService.js